import React, { useState } from "react";
import { Box, theme } from "@chakra-ui/react";
// @uiw/react-codemirror — React wrapper around CodeMirror 6. Provides the
// <CodeMirror> component and re-exports EditorView for theme/extension access.
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
// @codemirror/view — provides view-level extensions: lineNumbers() renders the
// gutter; EditorView.lineWrapping enables soft line wrap.
import { lineNumbers } from "@codemirror/view";
import { jinjaLanguage, jinjaHighlighting } from "modules/eva/utils/jinjaLanguage";

const FONT_FAMILY = "'Monaco', 'Menlo', 'Ubuntu Mono', monospace";
const FONT_SIZE = "13px";
const LINE_HEIGHT = "21px";

// Shorthand so color references below read like Chakra prop values.
const { colors } = theme;

/**
 * Base CodeMirror theme — visual appearance only, no token colors.
 * Defined at module level so it is created once, not on every render.
 */
const CM_BASE_THEME = EditorView.theme({
  // Fill the wrapper Box completely. Without this the editor collapses to the
  // height of its content (auto) and ignores the parent container's height.
  "&": {
    height: "100%",
    backgroundColor: colors.white,
  },
  // Apply our monospace font stack to the scrollable area. CodeMirror's default
  // font is inherited from the page body, which is a proportional font and would
  // misalign characters relative to syntax highlighting spans.
  ".cm-scroller": {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
  },
  // CodeMirror adds its own blue focus outline on .cm-editor. The Chakra Box
  // wrapper already provides a border, so we suppress CM's outline to avoid
  // a double-border on focus.
  "&.cm-focused": {
    outline: "none",
  },
  // Vertical padding lives on .cm-content (not on individual lines) so that
  // the top and bottom of the editor have breathing room.
  // textAlign:left is required because EditorView.lineWrapping causes wrapped
  // lines to inherit a centered alignment from the scroller in some browsers.
  ".cm-content": {
    padding: "8px 0",
    caretColor: colors.gray[900],
    textAlign: "left",
  },
  // Horizontal padding goes on each line so the gutter sits flush left while
  // text is inset from the gutter border. textAlign is inherited from
  // .cm-content above — no need to repeat it here.
  ".cm-line": {
    padding: "0 12px",
    color: colors.gray[900],
  },
  // Style the line-number gutter to match the Eva UI (light gray background,
  // subtle separator border, muted number color). userSelect:none prevents
  // accidental selection of line numbers when triple-clicking to select a line.
  ".cm-gutters": {
    backgroundColor: colors.gray[50],
    borderRight: `1px solid ${colors.gray[100]}`,
    color: colors.gray[400],
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE,
    userSelect: "none",
  },
  // Right-align line numbers within the gutter and give them the same
  // line-height as the content rows so numbers stay vertically centred.
  ".cm-lineNumbers .cm-gutterElement": {
    padding: "0 10px 0 8px",
    minWidth: "40px",
    lineHeight: LINE_HEIGHT,
    textAlign: "right",
  },
  // Match the caret color to the editor text color.
  ".cm-cursor, .cm-dropCursor": {
    borderLeftColor: colors.gray[900],
  },
  // position:absolute takes the placeholder out of normal flow so .cm-line
  // measures as 1 line tall (21px). Without this, a wrapping placeholder
  // inflates .cm-line to e.g. 42px, and CodeMirror re-applies that height
  // to the cursor via inline style on every view update — making the cursor
  // span two lines. pointerEvents:none preserves click-to-focus behavior.
  ".cm-placeholder": {
    position: "absolute",
    pointerEvents: "none",
    color: colors.gray[400],
    fontStyle: "normal",
    textAlign: "left",
  },
  // Thin 4px scrollbar — the default OS scrollbar is too wide for a compact
  // code editor panel. Webkit-only; Firefox scrollbar width is not customisable
  // per element and stays at the browser default.
  ".cm-scroller::-webkit-scrollbar": {
    width: "4px",
    height: "4px",
  },
  ".cm-scroller::-webkit-scrollbar-track": {
    background: "transparent",
  },
  ".cm-scroller::-webkit-scrollbar-thumb": {
    background: colors.gray[300],
    borderRadius: "2px",
  },
  ".cm-scroller::-webkit-scrollbar-thumb:hover": {
    background: colors.gray[400],
  },
});

// Fixed at module level — no props affect them, so there is no reason to
// recreate them per render or per component instance.
const CM_EXTENSIONS = [jinjaLanguage, jinjaHighlighting, lineNumbers(), EditorView.lineWrapping, CM_BASE_THEME];

export interface JinjaEditorProps {
  value: string;
  onChange?: (value: string) => void;
  isReadOnly?: boolean;
  placeholder?: string;
}

/**
 * A Jinja-syntax-highlighted code editor built on CodeMirror 6.
 *
 * Cursor position, selection, line numbers, and scroll are all native to
 * CodeMirror — no two-div sync needed. The custom StreamLanguage in
 * jinjaLanguage.ts only tokenizes {# #}, {% %}, and {{ }} constructs;
 * everything else returns null so surrounding prose stays plain black.
 */
const JinjaEditor: React.FC<JinjaEditorProps> = ({ value, onChange, isReadOnly = false, placeholder }) => {
  const [focused, setFocused] = useState(false);

  return (
    <Box
      height="100%"
      width="100%"
      borderWidth="1px"
      borderRadius="md"
      borderColor={focused ? "blue.300" : "gray.200"}
      boxShadow={focused ? `0 0 0 1px ${colors.blue[300]}` : "none"}
      overflow="hidden"
      sx={{
        // Propagate the container height into CodeMirror's internal DOM layers.
        "& > div": { height: "100%" },
        ".cm-editor": { height: "100%" },
        // CodeMirror sets placeholder white-space as an inline style; only
        // !important in a stylesheet can override an inline style.
        "& .cm-placeholder": { whiteSpace: "pre-wrap !important" },
        // In read-only mode, prevent CodeMirror from switching the pointer to
        // the text-insertion cursor (|) when hovering over or clicking content.
        ...(isReadOnly && {
          ".cm-content, .cm-line, .cm-scroller": { cursor: "default" },
        }),
      }}
    >
      <CodeMirror
        value={value}
        onChange={onChange}
        readOnly={isReadOnly}
        placeholder={placeholder}
        theme="none"
        extensions={CM_EXTENSIONS}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        basicSetup={{
          lineNumbers: false, // provided by explicit lineNumbers() extension
          foldGutter: false,
          highlightActiveLine: false,
          highlightSelectionMatches: false,
          closeBrackets: false, // avoid auto-closing { which interferes with {{ and {%
          bracketMatching: false,
          drawSelection: false, // uses native browser selection color
        }}
      />
    </Box>
  );
};

export default JinjaEditor;
